import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Copy, KeyRound, Plus, Radio, Trash2, X } from "lucide-react";
import type { AgentActivityEvent, McpTokenPublic } from "@outpost/shared";
import { MCP_DEFAULT_SCOPES } from "@outpost/shared";

async function apiGet<T>(
  path: string,
  token: string,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  try {
    const headers: Record<string, string> = {};
    if (token && token !== "cookie-session") headers.Authorization = `Bearer ${token}`;
    const res = await fetch(path, {
      headers,
      credentials: "include",
    });
    if (!res.ok) return { ok: false, error: await res.text() };
    return { ok: true, data: (await res.json()) as T };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

async function apiPost<T>(
  path: string,
  token: string,
  body?: unknown,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token && token !== "cookie-session") headers.Authorization = `Bearer ${token}`;
    const res = await fetch(path, {
      method: "POST",
      headers,
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) return { ok: false, error: await res.text() };
    return { ok: true, data: (await res.json()) as T };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

type Props = {
  token: string;
};

function isActiveRecently(lastUsedAt: string | null): boolean {
  if (!lastUsedAt) return false;
  const t = Date.parse(lastUsedAt.includes("T") ? lastUsedAt : `${lastUsedAt}Z`);
  if (Number.isNaN(t)) return false;
  return Date.now() - t < 60_000;
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "never";
  const d = Date.parse(iso.includes("T") ? iso : `${iso}Z`);
  if (Number.isNaN(d)) return iso;
  return new Date(d).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AgentsPane({ token }: Props) {
  const [tokens, setTokens] = useState<McpTokenPublic[]>([]);
  const [events, setEvents] = useState<AgentActivityEvent[]>([]);
  const [name, setName] = useState("Grok");
  const [scopes, setScopes] = useState(MCP_DEFAULT_SCOPES);
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [recipes, setRecipes] = useState<{
    endpoint: string;
    agentInstallPrompt: string;
    agentsMdSnippet: string;
    hookRecipe: string;
    recipes: Array<{ client: string; steps: string[] }>;
  } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [statusErr, setStatusErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const refreshTokens = useCallback(async () => {
    const res = await apiGet<{ tokens: McpTokenPublic[] }>(
      "/v1/mcp-tokens",
      token,
    );
    if (res.ok) setTokens(res.data.tokens);
  }, [token]);

  const refreshActivity = useCallback(async () => {
    const res = await apiGet<{ events: AgentActivityEvent[] }>(
      "/v1/agent-activity?limit=40",
      token,
    );
    if (res.ok) setEvents(res.data.events);
  }, [token]);

  const buildInstallPrompt = useCallback(() => {
    const base =
      recipes?.agentInstallPrompt ??
      `Install Outpost16 MCP at ${recipes?.endpoint ?? "https://outpost16.com/mcp"} with Bearer op_mcp_…`;
    if (freshToken) {
      return base.replaceAll("<OP_MCP_TOKEN>", freshToken);
    }
    return base;
  }, [recipes, freshToken]);

  const flash = (msg: string, err = false) => {
    setStatus(msg);
    setStatusErr(err);
  };

  const copyInstallPrompt = useCallback(async () => {
    const text = buildInstallPrompt();
    try {
      await navigator.clipboard.writeText(text);
      flash(
        freshToken
          ? "Install prompt copied (token included)."
          : "Install prompt copied — create a token first, then copy again to embed it.",
      );
    } catch {
      flash(
        "Clipboard blocked — select the prompt below and copy manually.",
        true,
      );
    }
  }, [buildInstallPrompt, freshToken]);

  useEffect(() => {
    void refreshTokens();
    void refreshActivity();
    void (async () => {
      const res = await apiGet<{
        endpoint: string;
        agentInstallPrompt: string;
        agentsMdSnippet: string;
        hookRecipe: string;
        recipes: Array<{ client: string; steps: string[] }>;
      }>("/v1/byoa/recipes", token);
      if (res.ok) setRecipes(res.data);
    })();
  }, [token, refreshTokens, refreshActivity]);

  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;
    setLive(true);
    void (async () => {
      try {
        const res = await fetch("/v1/agent-activity/stream", {
          headers: token && token !== "cookie-session" ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" as RequestCredentials,
          signal: ac.signal,
        });
        if (!res.ok || !res.body) {
          setLive(false);
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const parts = buf.split("\n\n");
          buf = parts.pop() || "";
          for (const part of parts) {
            if (!part.includes("event: agent_activity")) continue;
            const line = part.split("\n").find((l) => l.startsWith("data: "));
            if (!line) continue;
            try {
              const payload = JSON.parse(line.slice(6)) as AgentActivityEvent;
              setEvents((prev) => {
                if (prev.some((e) => e.id === payload.id)) return prev;
                return [payload, ...prev].slice(0, 60);
              });
              void refreshTokens();
            } catch {
              /* ignore malformed */
            }
          }
        }
      } catch {
        /* aborted or offline */
      } finally {
        setLive(false);
      }
    })();
    return () => ac.abort();
  }, [token, refreshTokens]);

  async function createToken() {
    setBusy(true);
    setFreshToken(null);
    const res = await apiPost<{
      id: string;
      token: string;
      name: string;
      scopes: string;
    }>("/v1/mcp-tokens", token, { name: name.trim() || "Agent", scopes });
    setBusy(false);
    if (!res.ok) {
      flash(res.error, true);
      return;
    }
    setFreshToken(res.data.token);
    flash(
      `Created “${res.data.name}” — copy the token, then copy the install prompt.`,
    );
    await refreshTokens();
  }

  async function revokeToken(id: string) {
    const res = await apiPost<{ ok: boolean }>(
      `/v1/mcp-tokens/${id}/revoke`,
      token,
    );
    if (!res.ok) {
      flash(res.error, true);
      return;
    }
    flash("Token revoked.");
    await refreshTokens();
  }

  const activeTokens = tokens.filter((t) => !t.revokedAt);

  return (
    <section className="op-pane op-agents op-agents-settings">
      <div className="op-panel-head">
        <div>
          <h1>Agents / MCP</h1>
          <small>
            Settings · BYOA tokens and live agent activity. Endpoint{" "}
            <code className="op-agents-code">
              {recipes?.endpoint ?? "/mcp"}
            </code>
          </small>
        </div>
        <div className="op-agents-head-actions">
          <span className={`op-tag${live ? " op-good" : " op-wait"}`}>
            <Radio size={12} aria-hidden />
            {live ? "Live" : "Reconnecting…"}
          </span>
          <button
            type="button"
            className="op-primary"
            aria-expanded={createOpen}
            onClick={() => setCreateOpen((o) => !o)}
          >
            {createOpen ? (
              <>
                <X size={14} aria-hidden />
                Close
              </>
            ) : (
              <>
                <Plus size={14} aria-hidden />
                Create token
              </>
            )}
          </button>
        </div>
      </div>

      {status ? (
        <p className={statusErr ? "op-notice err" : "op-notice"} role="status">
          {status}
        </p>
      ) : null}

      {createOpen ? (
        <div className="op-panel op-agents-setup">
          <div className="op-panel-head">
            <div>
              <h2>Create agent token</h2>
              <small>Shown once — copy it before leaving this panel.</small>
            </div>
            <span className="op-node-icon" aria-hidden>
              <KeyRound size={18} />
            </span>
          </div>

          <label className="op-conn-field">
            <span className="op-conn-field-label">Agent name</span>
            <input
              className="op-field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Grok, Cursor"
              aria-label="Agent name"
              disabled={busy}
            />
          </label>

          <label className="op-conn-field">
            <span className="op-conn-field-label">Scopes</span>
            <input
              className="op-field"
              value={scopes}
              onChange={(e) => setScopes(e.target.value)}
              placeholder={MCP_DEFAULT_SCOPES}
              aria-label="Scopes"
              disabled={busy}
              spellCheck={false}
            />
            <span className="op-conn-field-hint">
              Comma-separated. Default gates the usual quest + progress tools.
            </span>
          </label>

          <div className="op-agents-actions">
            <button
              type="button"
              className="op-primary"
              disabled={busy || !name.trim()}
              onClick={() => void createToken()}
            >
              {busy ? "Creating…" : "Create token"}
            </button>
            <button
              type="button"
              className="op-btn"
              onClick={() => void copyInstallPrompt()}
            >
              <Copy size={14} aria-hidden />
              Copy install prompt
            </button>
          </div>

          {freshToken ? (
            <div className="op-agents-fresh">
              <div className="op-agents-fresh-head">
                <strong>New token — copy now</strong>
                <button
                  type="button"
                  className="op-btn"
                  onClick={() =>
                    void navigator.clipboard.writeText(freshToken).then(
                      () => flash("Token copied."),
                      () => flash("Clipboard blocked.", true),
                    )
                  }
                >
                  <Copy size={14} aria-hidden />
                  Copy token
                </button>
              </div>
              <code className="op-agents-token">{freshToken}</code>
              <p>
                Paste into Grok / Cursor / ChatGPT with the install prompt. The
                raw token is only shown once.
              </p>
            </div>
          ) : (
            <p className="op-agents-hint">
              Create a token, then copy the install prompt so the agent gets
              URL, auth, and BYOA rules in one paste.
            </p>
          )}
        </div>
      ) : null}

      <div className="op-panel op-agents-connected">
        <div className="op-panel-head">
          <div>
            <h2>Connected agents</h2>
            <small>
              {activeTokens.length
                ? `${activeTokens.length} active`
                : "No active tokens yet"}
            </small>
          </div>
        </div>

        {activeTokens.length === 0 ? (
          <div className="op-agents-empty">
            <Bot size={22} aria-hidden />
            <p>
              No active tokens. Use Create token (top right), then paste the
              install prompt into your agent.
            </p>
          </div>
        ) : (
          <ul className="op-agents-token-list">
            {activeTokens.map((t) => (
              <li key={t.id}>
                <div className="op-node-icon" aria-hidden>
                  <Bot size={18} />
                </div>
                <div className="op-item-copy">
                  <h3>
                    {t.name}
                    {isActiveRecently(t.lastUsedAt) ? (
                      <span className="op-tag op-good">active now</span>
                    ) : null}
                  </h3>
                  <small>
                    Last used {formatWhen(t.lastUsedAt)}
                    <span className="op-agents-scopes"> · {t.scopes}</span>
                  </small>
                </div>
                <button
                  type="button"
                  className="op-btn ghost"
                  onClick={() => void revokeToken(t.id)}
                  aria-label={`Revoke ${t.name}`}
                  title="Revoke"
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="op-panel op-agents-activity">
        <div className="op-panel-head">
          <div>
            <h2>Agent activity</h2>
            <small>Tool calls and report_progress stream here live.</small>
          </div>
          <span className="op-tag">SSE</span>
        </div>

        {events.length === 0 ? (
          <div className="op-agents-empty">
            <Radio size={22} aria-hidden />
            <p>Waiting for the first MCP call from a connected agent.</p>
          </div>
        ) : (
          <ul className="op-agents-feed">
            {events.map((e) => (
              <li key={e.id}>
                <div className="op-item-copy">
                  <h3>
                    <span className="op-agents-actor">
                      {e.agentName ?? "founder"}
                    </span>
                    <span className="op-agents-action">{e.action}</span>
                  </h3>
                  <small>
                    {formatWhen(e.createdAt)}
                    {e.meta && typeof e.meta.message === "string"
                      ? ` — ${e.meta.message}`
                      : ""}
                  </small>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {recipes ? (
        <div className="op-panel op-agents-recipes">
          <div className="op-panel-head">
            <div>
              <h2>Client recipes</h2>
              <small>How to wire common agent GUIs to Outpost MCP.</small>
            </div>
          </div>

          <div className="op-agents-recipe-grid">
            {recipes.recipes.map((r) => (
              <div key={r.client} className="op-agents-recipe-card">
                <h3>{r.client}</h3>
                <ol>
                  {r.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>
            ))}
          </div>

          <div className="op-agents-prompt-head">
            <h3>Install prompt preview</h3>
            <button
              type="button"
              className="op-btn"
              onClick={() => void copyInstallPrompt()}
            >
              <Copy size={14} aria-hidden />
              Copy
            </button>
          </div>
          <pre className="op-agents-prompt">{buildInstallPrompt()}</pre>
        </div>
      ) : null}
    </section>
  );
}
